//@import dependencies
import React, {useState, useEffect} from 'react'
import { useTranslation } from 'react-i18next'
import {AiOutlineSafetyCertificate, AiFillCloseCircle, AiOutlineShareAlt} from 'react-icons/ai'
import {FiExternalLink} from 'react-icons/fi'
import {FaFileDownload} from 'react-icons/fa'
import { Spinner } from 'react-bootstrap'
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form'
//@end

//@import components
import CertificationsSkeleton from './certificationsSkeleton'
import DataTableComponent from 'app_components/commons/dataTable'
//@end

// @import hooks
import {useColors} from 'app_hooks/useColors'
// @end

// @INFO Import types
import { IPagination } from 'app_types/pagination/paginationTypes'
import {ICertificationItem} from 'app_types/admin/courseScheduling/courseSchedulingEnrollmentTypes'
// @end

// @INFO Import services
import CertificateService from 'app_services/user/certificateService'
import CourseSchedulingEnrollmentService from 'app_services/admin/courseScheduling/courseSchedulingEnrollmentService'
// @end

// @INFO Import Styles
import './../certificationsModule.scss'
import InputComponent from 'app_components/commons/Input/InputComponent'
import { BiSearchAlt2 } from 'react-icons/bi'
import CopyTextComponent from 'app_components/commons/CopyTextComponent/CopyTextComponent'
import { ICompanyUser } from 'app_types/admin/user/userTypes';
import TooltipComponent from 'app_components/commons/TooltipComponent/TooltipComponent'

// @ADD Component types
interface ICertificationsModuleLayoutProps {
  status: null | 'loading' | 'error'
  certificationsData: Array<any>
  pagination: IPagination
  company?: ICompanyUser
  onPageChange: (data: { page: number, search?: string, searchDoc?: string }) => void
  onSearch: (search: string) => void
  onSearchDoc: (val: string) => void
}

const CertificationsModuleLayout: React.FC<ICertificationsModuleLayoutProps> = (props) => {

  const [colors] = useColors()
  const certificateService: CertificateService = new CertificateService()
  const courseSchedulingEnrollmentService: CourseSchedulingEnrollmentService = new CourseSchedulingEnrollmentService();

  const { t } = useTranslation()
  const formHook = useForm()

  const [isCheckAll, setIsCheckAll] = useState<boolean>(false);
  const [isCheck, setIsCheck] = useState<Array<any>>([]);
  const [serviceCode, setServiceCode] = useState<string | undefined>(undefined);
  const [firstFetch, setFirstFetch] = useState<boolean>(false);

  const [downloading, setDownloading] = useState<{
    status: 'initial' | 'loading'
  }>({
    status: 'initial'
  })

  const [pdfPreview, setPdfPreview] = useState<{
    status: 'initial' | 'loaded' | 'loading',
    file: any | null
  }>({status: 'initial', file: null})

  const [pdfDownload, setPdfDownload] = useState<{
    status: 'initial' | 'loaded' | 'loading',
    file: any | null
  }>({status: 'initial', file: null})

  useEffect(() => {
    if (serviceCode?.trim()) {
      props.onSearch({search: serviceCode} as any)
      setFirstFetch(true);
    }
  }, [serviceCode])

  const handlePreviewFile = async (status: boolean, certificate?: ICertificationItem) => {
    if (status === true && certificate) {
      setPdfPreview({status: 'loading', file: null})
      if (certificate?.certificate?.imagePath) {
        setPdfPreview({status: 'loaded', file: certificate.certificate.imagePath})
      } else {
        const responsePreview = await certificateService.loadPreview(certificate.certificate.hash, {
          format: 1,
          template: 1,
          updateCertificate: true,
          certificate_queue: certificate._id
        })
        if (responsePreview && responsePreview.certificate?.imagePath) {
          setPdfPreview({status: 'loaded', file: responsePreview.certificate?.imagePath})
          certificate.certificate.imagePath = responsePreview.certificate?.imagePath
        } else {
          setPdfPreview({status: 'initial', file: null})
        }
      }
    } else {
      setPdfPreview({status: 'initial', file: null})
    }
  }

  const handleDownloadFile = async (status: boolean, certificate?: ICertificationItem) => {
    if (status === true && certificate) {
      setPdfDownload({status: 'loading', file: null})
      if (certificate.certificate.pdfPath) {
        setPdfDownload({status: 'loaded', file: certificate.certificate.pdfPath})
        window.open(certificate.certificate.pdfPath, '_blank');
      } else {
        const responsePreview = await certificateService.loadPreview(certificate.certificate.hash, {
          format: 2,
          template: 1,
          updateCertificate: true,
          certificate_queue: certificate._id,
        })
        if (responsePreview && responsePreview.certificate?.pdfPath) {
          setPdfDownload({status: 'loaded', file: responsePreview.certificate?.pdfPath})
          certificate.certificate.pdfPath = responsePreview.certificate?.pdfPath
          window.open(responsePreview.certificate?.pdfPath, '_blank');
        } else {
          setPdfDownload({status: 'initial', file: null})
        }
      }
    } else {
      setPdfDownload({status: 'initial', file: null})
    }
  }

  const downloadMasive = async () => {

    const values = formHook.getValues();
    if (!values.download) return null
    if (!Array.isArray(values.download)) values.download = [values.download]
    if (values.download.length === 0) return null;

    setDownloading({status: 'loading'})

    const responseDownload = await courseSchedulingEnrollmentService.downloadMasiveCertifications({
      certification_queue: values.download,
      downloadAll: isCheckAll,
      serviceId: serviceCode?.length ? serviceCode : undefined,
      company: props.company?._id
    })
    if (responseDownload && responseDownload?.path) {
      window.open(responseDownload?.path, '_blank');
    }
    if (props.certificationsData.length === 1) {
      formHook.reset({download: null})
    } else {
      formHook.reset({download: []})
    }
    setIsCheckAll(false)
    setIsCheck([])
    setDownloading({status: 'initial'})
  }

  const handleDownloadClick = (e: any) => {
    const { id, checked } = e.target;
    setIsCheck([...isCheck, id]);
    if (!checked) {
      setIsCheck(isCheck.filter(item => item !== id));
    }
  };

  const handleSelectAll = (e: any) => {
    setIsCheckAll(!isCheckAll);
    setIsCheck(props.certificationsData.map(li => `download_${li._id}`));
    if (isCheckAll) {
      setIsCheck([]);
    }
  };

  const handleClickChangePage = (page: number) => {
    if (props.onPageChange) {
      props.onPageChange({ page, search: serviceCode?.trim()?.length ? serviceCode : undefined })
    }
  }

  const getDataTableData = () => {
    if (props.certificationsData?.length) {
      return props.certificationsData?.map((certificate) => ({
        ...certificate,
        certificate: {
          ...certificate.certificate,
          title: !!certificate?.certificateSetting
            ? certificate.certificateSetting.certificateName
            : certificate?.certificate?.title
        }
      }))
    } else {
      return []
    }
  }

  const renderLinkCertificate = (certification: any) => {
    let link = undefined
    if (certification?.certificate?.urlCredencial) {
      link = certification.certificate.urlCredencial
    } else if (certification?.certificate?.url) {
      link = certification?.certificate?.url
    }
    if (link) {
      return (
        <Link
          to={{ pathname: `${link}` }}
          target='_blank'
        >
          <button
            className='btn btn-link btn-sm'
            data-tip={t('modules.certifications.actions.validate')}
          >
            <FiExternalLink size="20px" color={colors.primary} />
          </button>
        </Link>
      )
    }
    return null;
  }

  return(
    <div className='certifications_board-module'>
      <div className="title">
        {t("modules.certifications_board_module.title")}
      </div>
      <hr />
      <div className="table-format-standar main-table">
        {props.status === 'loading' ? (
          <CertificationsSkeleton />
        ): (
          <>
              <div className="row">
                <div className={`${(['loaded', 'loading'].includes(pdfPreview.status)) ? 'col-8' : 'col-12'}`}>
                  <DataTableComponent
                    data={getDataTableData()}
                    emptyMessage={!firstFetch ? {message: 'Digite código del servicio'} : undefined}
                    headerKeys={[
                      (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: '8px'
                          }}
                        >
                          <TooltipComponent
                            text='Selecciona todos los certificados'
                            placement='top'
                          >
                            <input
                              type="checkbox"
                              value='download_all'
                              name={`download_all`}
                              id={`download_all`}
                              onChange={handleSelectAll}
                              checked={isCheckAll}
                            />
                          </TooltipComponent>
                        </div>
                      ),
                      `${t('modules.certifications_board_module.headers.service_id')}`,
                      `${t('modules.certifications_board_module.headers.program_name')}`,
                      `${t('modules.certifications_board_module.headers.student_name')}`,
                      `${t('modules.certifications_board_module.headers.student_doc_number')}`,
                      `Auxiliar`,
                      `${t('modules.certifications_board_module.headers.date')}`,
                    ]}
                    keys={['courseId.metadata.service_id', 'certificate.title','userId.fullname','userId.profile.doc_number','auxiliar.fullname', 'date']}
                    tooltipKeys={['certificate.title']}
                    detailsKey={[]}
                    pagination={props.pagination}
                    onModal={null}
                    onPageChange={handleClickChangePage}
                    headerButtons={() => {
                      return (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                          }}
                        >
                          <InputComponent
                            placeholder='Digite código del servicio'
                            name='search'
                            onEnter={(val) => setServiceCode(val)}
                            defaultValue={serviceCode}
                          >
                            <InputComponent.Icon>
                              <BiSearchAlt2/>
                            </InputComponent.Icon>
                          </InputComponent>
                          <InputComponent
                            placeholder='No. Identidad'
                            name='searchDoc'
                            onEnter={(val) => props.onSearchDoc(val as string)}
                          >
                            <InputComponent.Icon>
                              <BiSearchAlt2/>
                            </InputComponent.Icon>
                          </InputComponent>
                          <button
                            className='btn btn-link btn-sm'
                            data-tip={t('modules.certifications.actions.download_masive')}
                            onClick={() => downloadMasive()}
                            disabled={downloading.status === 'loading'}
                          >
                            <>
                              {downloading.status === 'loading' && (
                                <>
                                  <Spinner className='spinner-loading' animation='border' size='sm' />
                                  &nbsp;&nbsp;
                                </>
                              )}
                              <FaFileDownload size="20px" color={colors.primary} />&nbsp;&nbsp;
                              {t('modules.certifications.actions.download_masive')}
                            </>
                          </button>
                        </div>
                      )
                    }}
                    rowButtons={(certification) => {
                      return (
                        <div className='d-flex align-items-center' >
                          {/* <button
                            className='btn btn-link btn-sm'
                            data-tip={t('modules.modal_user_certifications.actions.preview')}
                            onClick={() => handlePreviewFile(true, certification)}
                            disabled={pdfPreview.status === 'loading'}
                          >
                            <AiOutlineSafetyCertificate size="20px" color={colors.primary} />
                          </button> */}
                          <CopyTextComponent hideText text={certification?.certificate?.url} />
                          <button
                            className='btn btn-link btn-sm'
                            data-tip={t('modules.modal_user_certifications.actions.download')}
                            onClick={() => handleDownloadFile(true, certification)}
                            disabled={pdfDownload.status === 'loading'}
                          >
                            {pdfDownload.status === 'loading' ? (
                              <Spinner className='spinner-loading' animation='border' size='sm' />
                            ): (
                              <FaFileDownload size="20px" color={colors.primary} />
                            )}
                          </button>
                          {renderLinkCertificate(certification)}
                        </div>
                      )
                    }}
                    firstColRowButtons={(certification) => {
                      return (
                        <>
                          {certification.certificate?.pdfPath && (
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                value={certification._id}
                                name={`download`}
                                id={`download_${certification._id}`}
                                onChange={handleDownloadClick}
                                ref={formHook.register()}
                                checked={isCheck.includes(`download_${certification._id}`)}
                              />
                            </div>
                          )}
                        </>
                      )
                    }}
                  />
                </div>
                {pdfPreview.status === 'loaded' && pdfPreview.file ? (
                  <div className='col-4'>
                    <hr />
                    <div className="text-right">
                      <button
                        className='btn btn-link btn-sm'
                        data-tip={t('modules.modal_user_certifications.actions.close_preview')}
                        onClick={() => handlePreviewFile(false)}
                      >
                        <AiFillCloseCircle size="20px" color={colors.primary} />
                      </button>
                    </div>
                    <img style={{width: '100%'}} src={pdfPreview.file} alt='preview'/>
                  </div>
                ): (
                  <>
                    {pdfPreview.status === 'loading' && (
                      <div className='text-center'>
                        <Spinner animation="border" className='icon-loading' />
                      </div>
                    )}
                  </>
                )}

              </div>
            {/* {props.certificationsData && Array.isArray(props.certificationsData) && props.certificationsData.length > 0 ? null : (
              <AlertComponent
                type='warning'
                title={t('modules.certifications_board_module.no_data')}
              />
            )} */}
          </>
        )}
      </div>
    </div>
  )
}

export default CertificationsModuleLayout
